// src/App.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MaintenanceProvider } from "./context/MaintenanceContext";
import { ThemeProvider as CustomThemeProvider } from "./context/ThemeContext"; // ✅ ALREADY IMPORTED
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Sidebar from "./Sidebar";
import SidebarLayout from "./SidebarLayout";
import AdminDashboard from "./components/AdminDashboard";
import Dashboard from "./components/Dashboard";
import TestingProjectDetails from "./components/TestingProjectDetails";
import IssueProjectSelection from "./components/IssueProjectSelection";
import TestingProjectSelection from "./components/TestingProjectSelection";
import ReportProjectSelection from "./components/ReportProjectSelection";
import ProjectSelection from "./components/ProjectSelection";
import CreateProject from "./components/CreateProject";
import ProjectActivation from "./components/projectDetails/ProjectActivation";
import RepoActivation from "./components/projectDetails/RepoActivation";
import EditProject from "./components/EditProject";
import ProjectDetails from "./components/ProjectDetails";
import RunDetails from "./components/RunDetails";
import IssueSelection from "./components/IssueSelection";
import IssueDetails from "./components/IssueDetails";
import UTGen from "./components/UTGen";
import PullRequestReview from "./components/PullRequestReview";
import SocketTest from "./components/projectDetails/SocketTest";
import CoverageReport from "./components/CoverageReport";
import BuildDetails from "./components/BuildDetails";
import Pipeline from "./components/Pipeline";
import LoginPage from "./components/Login";
import SignUpPage from "./components/SignUp";
import ForgotPassword from "./components/ForgotPassword";
import ProtectedRoute from "./components/ProtectedRoute";
import TokenExpiryModal from "./components/auth/TokenExpiryModal";
import IssueProjectDetails from "./components/IssueProjectDetails";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Profile from "./components/Profile";
import MemoryLeakDetails from "./components/projectDetails/MemoryDetails";
import LocDetails from "./components/LocDetails";
import OfflineBar from "./components/utilities/OfflineBar";
import StatusBar from "./components/utilities/StatusBar";
import NotFound from "./components/NotFound";
import CodeDiffViewer from "./components/staticAnalysis/CodeDiffViewer";
import aws from "./img/Amazon-Web-Services-AWS-Logo.png";
import profile from "./img/profile-image.png";
import { AuthProvider, useAuth } from "./context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import { ToastContainer } from 'react-toastify';
import {
  CssBaseline,
  Box,
  Button,
  Toolbar,
  AppBar,
  Typography,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./styles/app.css";
import ReportingRunDetails from "./components/ReportingRunDetails";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ProfileAvatar from "./components/ProfileAvtar";
import CheckoutPage from "./components/CheckOut";
import Popup from "./components/PopUp";

const getInitialMode = () => {
  const savedMode = localStorage.getItem("themeMode");
  return savedMode ? savedMode : "light";
};

// Global error dialog component that appears on any API fetch error
const GlobalErrorDialog = ({ errorMessage, clearError }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLoginAgain = () => {
    localStorage.removeItem("access_token");
    clearError();
    navigate("/login");
  };

  return (
    <Dialog open={Boolean(errorMessage)}>
      <DialogTitle>
        Error
        <IconButton
          aria-label="close"
          onClick={clearError}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            ":hover": {
              color: "white",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{errorMessage}</Typography>
        <Typography variant="caption" display="block" sx={{ mt: 2 }}>
          Having Problems?{" "}
          <Link
            href="https://athrvcloud.com/contact/"
            target="_blank"
            rel="noopener"
          >
            Contact Us
          </Link>
        </Typography>
      </DialogContent>
      <DialogActions>
        {location.pathname !== "/login" && (
          <Button
            onClick={handleLoginAgain}
            variant="contained"
            className="athrv-dash-primary-button"
          >
            Login Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

function AppContent({ }) {
  const { isAuthenticated, planId } = useAuth();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [selectedMenuItem, setSelectedMenuItem] = useState("dashboard");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [userData, setUserData] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");
  const userName = localStorage.getItem("fname") || "Guest";
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [showGrettingsAndSearch, setShowGrettingsAndSearch] = useState(false);
  const [mode, setMode] = useState(getInitialMode);

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  // ADD THIS useEffect - Syncs theme with body class for CSS
  useEffect(() => {
    if (mode === "dark") {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [mode]);

  const theme1 = useMemo(
    () =>
      createTheme({
        palette: {
          mode: mode,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                color: mode === "dark" ? "white" : undefined,
              },
            },
          },
        },
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };
  
  const isDarkMode = theme1.palette.mode === "dark";

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/" ||
    location.pathname.startsWith("/repo/activate");

  const showLayout = isAuthenticated && !isAuthPage;

  const handleLogout = async () => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/logout`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("fname");
          navigate("/login");
        } else {
          console.error("Logout failed:", response.statusText);
        }
      } catch (error) {
        if (error.message === "Failed to fetch") {
          console.error("Logout error:", error);
        }
      }
    } else {
      console.warn("No token found, redirecting to login");
      navigate("/login");
    }
  };

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const toggleMobileSidebar = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleMenuToggle = () => {
    setShowGrettingsAndSearch((prev) => !prev);
  };

  const seeProfile = () => {
    navigate("/profile");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.querySelector(".mobile-sidebar");
      const drawerIcon = document.querySelector(".drawer-icon");
      if (
        mobileOpen &&
        sidebar &&
        !sidebar.contains(event.target) &&
        !drawerIcon.contains(event.target)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileOpen]);

  return (
    <ThemeProvider theme={theme1}>
      {/* ✅ WRAP WITH CUSTOM THEME PROVIDER */}
      <CustomThemeProvider mode={mode} toggleTheme={toggleTheme}>
        <Box sx={{ display: "flex", overflow: "visible" }}>
          <CssBaseline />
          {showLayout && (
            <>
              <AppBar
                position="fixed"
                sx={{
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                  left: isMobile ? 0 : isSidebarExpanded ? "250px" : "80px",
                  width: isMobile
                    ? "100%"
                    : `calc(100% - ${isSidebarExpanded ? "250px" : "80px"})`,
                  transition: "left 0.3s, width 0.3s",
                }}
              >
                <Toolbar
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    backgroundColor: isDarkMode ? "#202020" : "rgb(12, 20, 50)",
                    padding: isMobile ? "0 0 0 10px" : "0 5px 0 5px !important",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {isMobile ? (
                    <>
                      <IconButton
                        color="inherit"
                        onClick={toggleMobileSidebar}
                        className="drawer-icon"
                      >
                        <MenuIcon />
                      </IconButton>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          variant="body1"
                          sx={{ marginRight: "20px", color: "white" }}
                        >
                          Hi, {userName}
                        </Typography>
                        <IconButton
                          color="inherit"
                          onClick={handleSettingsClick}
                          sx={{ marginRight: "5px" }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </>
                  ) : (
                    <>
                      <SidebarLayout
                        isSidebarExpanded={isSidebarExpanded}
                        toggleSidebar={() =>
                          setIsSidebarExpanded(!isSidebarExpanded)
                        }
                      />
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            display: planId != "invalid" || planId == null ? "flex" : "none",
                            alignItems: "center",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            transition: "border 0.3s ease",
                            mr: 1,
                            "&:hover": {
                              border: "transparent",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              bgcolor: "#5ced73",
                              borderRadius: "50%",
                              mr: 1,
                            }}
                          />
                          <Box
                            component="img"
                            src={aws}
                            alt="AWS"
                            sx={{ height: 24, mr: 1 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: "white", mr: 1, fontSize: "13px" }}
                          >
                            Mumbai Server
                          </Typography>
                          <Typography variant="body1" sx={{ color: "#BBBCB6", fontSize: "12px" }}>
                            (ap-south-1)
                          </Typography>
                        </Box>

                        <Box sx={{ mr: 2 }}>
                          <ProfileAvatar />
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{ marginRight: "20px", color: "white" }}
                        >
                          Hi, {userName}
                        </Typography>

                        {/* Theme Toggle Icon */}
                        <IconButton color="inherit" onClick={toggleTheme}>
                          {mode === "light" ? (
                            <LightModeIcon />
                          ) : (
                            <DarkModeIcon />
                          )}
                        </IconButton>

                        <IconButton
                          color="inherit"
                          aria-label="settings"
                          onClick={handleSettingsClick}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}

                  <Menu
                    anchorEl={settingsAnchorEl}
                    open={Boolean(settingsAnchorEl)}
                    onClose={handleSettingsClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem onClick={seeProfile}>Profile</MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </Menu>

                  {isMobile && showGrettingsAndSearch && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "56px",
                        left: "0",
                        width: "100%",
                        bgcolor: "#0C1A32",
                        padding: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          bgcolor: "white",
                          borderRadius: "4px",
                          padding: "0 10px",
                          width: "100%",
                        }}
                      >
                        <SearchIcon sx={{ color: "gray" }} />
                        <TextField
                          variant="standard"
                          placeholder="Search..."
                          InputProps={{
                            disableUnderline: true,
                            style: { fontSize: "14px", marginLeft: "10px" },
                          }}
                          sx={{ width: "100%" }}
                        />
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{ color: "white", textAlign: "center" }}
                      >
                        Hi, {userName}
                      </Typography>
                    </Box>
                  )}
                </Toolbar>
              </AppBar>

              {!isMobile && (
                <Box
                  component="nav"
                  sx={{
                    width: isSidebarExpanded ? 250 : 80,
                    flexShrink: { sm: 0 },
                    background: "#0C1A32",
                    transition: "width 0.3s",
                  }}
                >
                  <Sidebar isSidebarExpanded={isSidebarExpanded} />
                </Box>
              )}
              {isMobile && mobileOpen && (
                <Box
                  component="nav"
                  className="mobile-sidebar"
                  sx={{
                    width: 250,
                    flexShrink: { sm: 0 },
                    background: "#0C1A32",
                  }}
                >
                  <Sidebar isSidebarExpanded={true} />
                </Box>
              )}
            </>
          )}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: isAuthPage ? 0 : 3,
              width: isMobile
                ? "100%"
                : `calc(100% - ${isAuthPage ? 0 : isSidebarExpanded ? 250 : 80}px)`,
              marginTop: !showLayout ? 0 : "64px",
              height: "auto",
              overflowY: isMobile ? "auto" : "visible",
              padding: 0,
            }}
          >
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/codediff" element={<CodeDiffViewer />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admindashboard"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/projects/:projectId/issues/:issueKey/pr-review" 
                element={<PullRequestReview />} 
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <ProjectSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/create"
                element={
                  <ProtectedRoute>
                    <CreateProject />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/activation/"
                element={
                  <ProtectedRoute>
                    <ProjectActivation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/edit"
                element={
                  <ProtectedRoute>
                    <EditProject />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/issues/:projectId"
                element={
                  <ProtectedRoute>
                    <IssueProjectDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/loc-details"
                element={
                  <ProtectedRoute>
                    <LocDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/testing/:projectId"
                element={
                  <ProtectedRoute>
                    <TestingProjectDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checkout/:planId"
                element={
                  <ProtectedRoute>
                    <CheckoutPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/build"
                element={
                  <ProtectedRoute>
                    <BuildDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rundetails/:runId"
                element={
                  <ProtectedRoute>
                    <RunDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rundetails/reporting/:runId"
                element={
                  <ProtectedRoute>
                    <ReportingRunDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pipeline/:runId"
                element={
                  <ProtectedRoute>
                    <Pipeline />
                  </ProtectedRoute>
                }
              />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="/choose-plan" element={<ProtectedRoute><Popup /></ProtectedRoute>} />
              <Route
                path="/issues/:projectId"
                element={
                  <ProtectedRoute>
                    <IssueSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/issues/:projectId/:issueKey"
                element={
                  <ProtectedRoute>
                    <IssueDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/cpputest/:projectId/:toolId"
                element={
                  <ProtectedRoute>
                    <CoverageReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/repo/activate"
                element={
                  <ProtectedRoute>
                    <RepoActivation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/issues"
                element={
                  <ProtectedRoute>
                    <IssueProjectSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/testing"
                element={
                  <ProtectedRoute>
                    <TestingProjectSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai/testing"
                element={
                  <ProtectedRoute>
                    <UTGen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai/test"
                element={
                  <ProtectedRoute>
                    <SocketTest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reporting"
                element={
                  <ProtectedRoute>
                    <ReportProjectSelection />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Box>
        </Box>
      </CustomThemeProvider>
    </ThemeProvider>
  );
}

function App() {
  const [status, setStatus] = useState(null);
  const [showBackOnlineBar, setShowBackOnlineBar] = useState(false);
  const [globalApiError, setGlobalApiError] = useState(null);

  const checkConnectivity = useCallback(async () => {
    try {
      if (navigator.onLine) {
        await fetch("https://www.google.com", { mode: "no-cors" });
        setStatus(null);
        setShowBackOnlineBar(true);
        setTimeout(() => setShowBackOnlineBar(false), 3000);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        if (response.status === 401) {
          const jwtToken = localStorage.getItem("access_token");
          let tokenExpired = true;
          try {
            const { exp } = jwtDecode(jwtToken);
            tokenExpired = Date.now() >= exp * 1000;
          } catch (error) {
            console.error("Invalid token format", error);
            tokenExpired = true;
          }

          if (tokenExpired && localStorage.getItem("remember_me") === "true") {
            const refreshResponse = await originalFetch(
              `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/refresh`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("refresh_token")}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              const newToken = refreshData.access_token;
              localStorage.setItem("access_token", newToken);

              const newArgs = [...args];
              if (newArgs[1] && newArgs[1].headers) {
                newArgs[1].headers["Authorization"] = `Bearer ${newToken}`;
              } else {
                newArgs[1] = {
                  ...newArgs[1],
                  headers: {
                    Authorization: `Bearer ${newToken}`,
                  },
                };
              }
              return await originalFetch(...newArgs);
            } else {
              throw new Error("Unable to refresh token");
            }
          }
        }

        if (!response.ok) {
          const url = args[0];
          const isAuthEndpoint = url.includes('/auth/register') || url.includes('/auth/login') || url.includes('/auth/verify-otp');
  
          if (!isAuthEndpoint && response.status >= 500) {
            setStatus({ message: response.statusText, type: "maintenance" });
          }
        }
        return response;
      } catch (error) {
        if (error.message === "Failed to fetch") {
          try {
            await originalFetch("https://www.google.com", { mode: "no-cors" });
            setStatus({ message: error.message, type: "maintenance" });
          } catch (connectivityError) {
            if (!navigator.onLine) {
              setStatus({ message: "No Internet Connection", type: "offline" });
            }
          }
        }
        throw error;
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    if (status && status.type === "offline") {
      setGlobalApiError(null);
      interval = setInterval(checkConnectivity, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, checkConnectivity]);

  return (
    <AuthProvider>
      <MaintenanceProvider>
        <ToastContainer />
        <Router>
          <StatusBar
            status={status}
            showBackOnline={showBackOnlineBar}
            onRetry={checkConnectivity}
            onClose={() => setStatus(null)}
          />
          <TokenExpiryModal />
          <GlobalErrorDialog
            errorMessage={globalApiError}
            clearError={() => setGlobalApiError(null)}
          />
          <AppContent />
        </Router>
      </MaintenanceProvider>
    </AuthProvider>
  );
}

export default App;